import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ChartBar, Files, Sparkle, Ruler, StackSimple, DownloadSimple, CheckCircle, Warning, FloppyDisk } from "@phosphor-icons/react";

const Stat = ({ icon, label, value, testid }) => (
  <div className="flex-1 min-w-[120px] border border-[var(--border)] rounded-[3px] p-3 bg-[var(--surface-2)]" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">{icon}{label}</div>
    <div className="text-2xl font-mono mt-1 text-[var(--text)]">{value}</div>
  </div>
);

const ProfRow = ({ label, items }) => {
  if (!items || !items.length) return null;
  return (
    <div className="flex gap-2 text-[11px]">
      <span className="shrink-0 w-24 font-mono text-[var(--text-dim)]">{label}</span>
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 20).map((v, i) => (
          <span key={`${v}-${i}`} className="font-mono text-[var(--text)] bg-[var(--surface-2)] border border-[var(--border)] rounded-[2px] px-1">{v}</span>
        ))}
      </div>
    </div>
  );
};

const EMPTY_STD = {
  title_block: { block: "", layer: "", tags: {} },
  csv_columns: "",
  curve_table: { line_columns: "", curve_columns: "" },
  branding: { firm: "", logo: "" },
  report_defaults: { project: "", parcel: "", prepared_by: "", job_no: "" },
};

const TAG_HINTS = {
  SHEET: ["sheetno", "shtno", "sheet_no", "sheet", "sht", "dwgno", "dwg_no", "sheetnum"],
  TOTAL: ["totalsheets", "total", "sheets", "ofsheets", "shttot"],
  TITLE: ["sheettitle", "title", "dwgtitle"],
  PROJECT: ["projectname", "project", "projname", "job", "jobname"],
  PROJNO: ["projectno", "projno", "jobno", "projectnum", "jobnum", "job_no", "proj_no"],
  DATE: ["date"],
  SCALE: ["scale"],
  DRAWN: ["drawnby", "drawn", "author", "by", "dwnby"],
  CHECKED: ["checkedby", "checked", "chkd", "chk", "checkd"],
  REV: ["revision", "rev", "revno"],
};

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export const ClientDashboard = ({ clientId, clientName, onSaveDefaults, standards, onSaveStandards }) => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [platSel, setPlatSel] = useState({ "Civil 3D": true, "progeCAD": true, "AutoCAD": true });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [std, setStd] = useState(EMPTY_STD);
  const [stdOpen, setStdOpen] = useState(false);
  const logoInput = useRef();

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 1024 * 1024) { toast.error("Logo must be under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setStd((p) => ({ ...p, branding: { ...p.branding, logo: reader.result } }));
    reader.readAsDataURL(f);
  };

  useEffect(() => {
    if (!open || !clientId) return;
    setS(null);
    api.clientSummary(clientId).then(setS).catch(() => toast.error("Could not load dashboard."));
    setStd(standards
      ? {
          ...EMPTY_STD, ...standards,
          title_block: { ...EMPTY_STD.title_block, ...(standards.title_block || {}), tags: { ...(standards.title_block?.tags || {}) } },
          curve_table: { ...EMPTY_STD.curve_table, ...(standards.curve_table || {}) },
          branding: { ...EMPTY_STD.branding, ...(standards.branding || {}) },
          report_defaults: { ...EMPTY_STD.report_defaults, ...(standards.report_defaults || {}) },
        }
      : EMPTY_STD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const TAG_FIELDS = [
    ["SHEET", "Sheet number"], ["TOTAL", "Total sheets"], ["TITLE", "Sheet title"],
    ["PROJECT", "Project name"], ["PROJNO", "Project/Job no."], ["DATE", "Date"],
    ["SCALE", "Scale"], ["DRAWN", "Drawn by"], ["CHECKED", "Checked by"], ["REV", "Revision"],
  ];
  const setTag = (k, v) => setStd((p) => ({ ...p, title_block: { ...p.title_block, tags: { ...p.title_block.tags, [k]: v } } }));
  const setTB = (k, v) => setStd((p) => ({ ...p, title_block: { ...p.title_block, [k]: v } }));

  const autofillFromCad = () => {
    const tb = s?.profile?.title_block;
    if (!tb || !(tb.block || (tb.tags && tb.tags.length))) {
      toast.info("No title block detected in this client's finished CAD yet. Upload/attach a finished DWG/DXF as a template first.");
      return;
    }
    const detected = tb.tags || [];
    const tags = {};
    for (const [field, hints] of Object.entries(TAG_HINTS)) {
      const hit = detected.find((t) => hints.some((h) => norm(t) === h))
        || detected.find((t) => hints.some((h) => norm(t).includes(h)));
      if (hit) tags[field] = hit;
    }
    setStd((p) => ({ ...p, title_block: { ...p.title_block, block: tb.block || p.title_block.block, tags: { ...p.title_block.tags, ...tags } } }));
    toast.success(`Autofilled from finished CAD (${Object.keys(tags).length} tag${Object.keys(tags).length === 1 ? "" : "s"} matched)`);
  };

  const buildExportUrl = () => {
    const params = new URLSearchParams();
    const sel = Object.entries(platSel).filter(([, v]) => v).map(([k]) => k);
    if (sel.length && sel.length < 3) params.set("platforms", sel.join(","));
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const qs = params.toString();
    return api.clientExportUrl(clientId) + (qs ? `?${qs}` : "");
  };
  const noPlat = !Object.values(platSel).some(Boolean);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="client-dashboard-btn" disabled={!clientId}
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)] disabled:opacity-40 px-2">
          <ChartBar size={14} weight="bold" /> Dashboard
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-xl rounded-[4px]" data-testid="client-dashboard-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><ChartBar size={18} weight="bold" className="text-[var(--primary)]" />{clientName || "Client"} — Dashboard</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">A snapshot of this client's library, standards and latest work.</DialogDescription>
        </DialogHeader>

        {!s && <div className="p-4 text-[12px] text-[var(--text-dim)] font-mono">Loading…</div>}
        {s && (
          <div className="flex flex-col gap-3">
            {s.profile_strength && (
              <div className="border border-[var(--border)] rounded-[4px] p-3" data-testid="profile-strength">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">CAD profile strength · grounding quality</span>
                  <span className={`text-[11px] font-mono uppercase ${s.profile_strength.level === "strong" ? "text-[var(--success)]" : s.profile_strength.level === "partial" ? "text-amber-500" : "text-[var(--alert)]"}`} data-testid="profile-strength-level">
                    {s.profile_strength.level} · {s.profile_strength.score}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-[var(--bg)] overflow-hidden">
                  <div className={`h-full transition-all ${s.profile_strength.level === "strong" ? "bg-[var(--success)]" : s.profile_strength.level === "partial" ? "bg-amber-500" : "bg-[var(--alert)]"}`}
                    style={{ width: `${s.profile_strength.score}%` }} />
                </div>
                {s.profile_strength.missing?.length > 0 && (
                  <div className="text-[10px] font-mono text-[var(--text-dim)] mt-1.5">Add for better grounding: {s.profile_strength.missing.join(", ")}</div>
                )}
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Stat icon={<Files size={12} weight="bold" />} label="Files" value={s.file_count} testid="dash-files" />
              <Stat icon={<Sparkle size={12} weight="bold" />} label="Routines" value={s.gen_count} testid="dash-gens" />
              <Stat icon={<Ruler size={12} weight="bold" />} label="Scale" value={s.scale ? `1"=${s.scale}'` : "—"} testid="dash-scale" />
              <Stat icon={<StackSimple size={12} weight="bold" />} label="Layers" value={s.layers?.length || 0} testid="dash-layers" />
            </div>

            <div className="border border-[var(--border)] rounded-[3px] p-3" data-testid="dash-last-gen">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] mb-1">Latest routine</div>
              {s.last_generation ? (
                <div>
                  <div className="text-[13px] flex items-start gap-1.5">
                    {s.last_generation.valid
                      ? <CheckCircle size={13} className="text-[var(--success)] mt-0.5 shrink-0" />
                      : <Warning size={13} className="text-[var(--alert)] mt-0.5 shrink-0" />}
                    <span className="line-clamp-2">{s.last_generation.prompt}</span>
                  </div>
                  <div className="text-[10px] font-mono text-[var(--text-dim)] mt-1">
                    {s.last_generation.platform} {s.last_generation.version} · {new Date(s.last_generation.created_at).toLocaleString()}
                  </div>
                </div>
              ) : <div className="text-[12px] text-[var(--text-dim)]">No routines generated yet.</div>}
            </div>

            {s.layers?.length > 0 && (
              <div className="border border-[var(--border)] rounded-[3px] p-3" data-testid="dash-layer-list">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] mb-2">Standard layers (from finished CAD)</div>
                <ScrollArea className="max-h-[100px]">
                  <div className="flex flex-wrap gap-1.5">
                    {s.layers.map((l) => (
                      <Badge key={l} variant="outline" className="rounded-[2px] text-[10px] font-mono border-[var(--border)] text-[var(--text-dim)]">{l}</Badge>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
            {s.profile && (s.profile.text_styles?.length || s.profile.blocks?.length || s.profile.point_styles?.length || s.profile.dim_styles?.length || s.profile.scale_std) ? (
              <div className="border border-[var(--border)] rounded-[3px] p-3 flex flex-col gap-2" data-testid="dash-profile">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">Client standard profile</div>
                {s.profile.scale_std && <ProfRow label="Scale" items={[s.profile.scale_std]} />}
                <ProfRow label="Text styles" items={s.profile.text_styles} />
                <ProfRow label="Dim styles" items={s.profile.dim_styles} />
                <ProfRow label="Blocks / symbols" items={s.profile.blocks} />
                <ProfRow label="Point styles" items={s.profile.point_styles} />
              </div>
            ) : null}

            <div className="border border-[var(--border)] rounded-[3px] p-3" data-testid="dash-trends">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] mb-2">Routines per week (last 8)</div>
              {(() => {
                const wk = s.weekly || [];
                const max = Math.max(1, ...wk.map((w) => w.count));
                return (
                  <div className="flex items-end gap-1.5 h-[70px]">
                    {wk.map((w) => (
                      <div key={w.week} className="flex-1 flex flex-col items-center gap-1 group" title={`Week of ${w.week}: ${w.count}`} data-testid={`dash-week-${w.week}`}>
                        <div className="text-[9px] font-mono text-[var(--text-dim)]">{w.count || ""}</div>
                        <div className="w-full rounded-[2px] bg-[var(--primary)]/70 group-hover:bg-[var(--primary)] transition-all"
                          style={{ height: `${Math.max(3, (w.count / max) * 52)}px` }} />
                        <div className="text-[8px] font-mono text-[var(--text-dim)]">{w.week.slice(5)}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="border border-[var(--border)] rounded-[3px] p-3 flex flex-col gap-2.5" data-testid="export-panel">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">Export routine package</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {["Civil 3D", "progeCAD", "AutoCAD"].map((p) => (
                  <button key={p} data-testid={`export-plat-${p.replace(/\s+/g, "-")}`}
                    onClick={() => setPlatSel((s2) => ({ ...s2, [p]: !s2[p] }))}
                    className={`h-6 px-2 rounded-[3px] border text-[10px] font-mono transition-colors ${platSel[p] ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>
                    {platSel[p] ? "✓ " : ""}{p}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono text-[var(--text-dim)]">
                <span>From</span>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="export-date-from"
                  className="flex-1 h-7 px-2 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] text-[10px] font-mono" />
                <span>To</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="export-date-to"
                  className="flex-1 h-7 px-2 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] text-[10px] font-mono" />
              </div>
              <a href={s.gen_count && !noPlat ? buildExportUrl() : undefined}
                 onClick={(e) => { if (!s.gen_count) { e.preventDefault(); toast.error("No routines to export yet."); } else if (noPlat) { e.preventDefault(); toast.error("Pick at least one platform."); } }}
                 data-testid="export-client-btn">
                <Button className="w-full h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" disabled={!s.gen_count || noPlat}>
                  <DownloadSimple size={16} weight="bold" /> Export routines (.zip, per-platform folders)
                </Button>
              </a>
            </div>
            {onSaveStandards && (
              <div className="border border-[var(--border)] rounded-[3px]" data-testid="standards-panel">
                <button onClick={() => setStdOpen((o) => !o)} data-testid="standards-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] hover:text-[var(--text)]">
                  <span>Client standards · title block &amp; estimating</span>
                  <span>{stdOpen ? "−" : "+"}</span>
                </button>
                {stdOpen && (
                  <div className="px-3 pb-3 flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-mono text-[var(--text-dim)]">Title block</span>
                      <button onClick={autofillFromCad} data-testid="std-autofill"
                        className="text-[10px] font-mono text-[var(--primary)] hover:underline">↻ Autofill from finished CAD</button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[9px] font-mono text-[var(--text-dim)] mb-1">Title-block BLOCK name</div>
                        <Input value={std.title_block.block} onChange={(e) => setTB("block", e.target.value)} data-testid="std-tb-block"
                          placeholder="e.g. TB-24x36" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                      </div>
                      <div>
                        <div className="text-[9px] font-mono text-[var(--text-dim)] mb-1">Title-block LAYER</div>
                        <Input value={std.title_block.layer} onChange={(e) => setTB("layer", e.target.value)} data-testid="std-tb-layer"
                          placeholder="e.g. G-ANNO-TTLB" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                      </div>
                    </div>
                    <div className="text-[9px] font-mono text-[var(--text-dim)]">Attribute tags (your block's real tag names — leave blank to use defaults)</div>
                    <div className="grid grid-cols-2 gap-2">
                      {TAG_FIELDS.map(([k, label]) => (
                        <div key={k} className="flex items-center gap-1.5">
                          <span className="w-20 shrink-0 text-[10px] font-mono text-[var(--text-dim)]">{label}</span>
                          <Input value={std.title_block.tags[k] || ""} onChange={(e) => setTag(k, e.target.value)} data-testid={`std-tag-${k}`}
                            placeholder={k} className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-[9px] font-mono text-[var(--text-dim)] mb-1">Estimating CSV columns (in order)</div>
                      <Input value={std.csv_columns} onChange={(e) => setStd((p) => ({ ...p, csv_columns: e.target.value }))} data-testid="std-csv-columns"
                        placeholder="Item No, Pay Item, Description, Quantity, Unit, Unit Price, Amount"
                        className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                    </div>
                    <div className="border-t border-[var(--border)] pt-2 flex flex-col gap-2">
                      <div className="text-[9px] font-mono text-[var(--text-dim)]">Curve/line table columns (headers, in order — blank = plat defaults)</div>
                      <Input value={std.curve_table.line_columns} onChange={(e) => setStd((p) => ({ ...p, curve_table: { ...p.curve_table, line_columns: e.target.value } }))} data-testid="std-line-cols"
                        placeholder="Line table: Line, Bearing, Distance" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                      <Input value={std.curve_table.curve_columns} onChange={(e) => setStd((p) => ({ ...p, curve_table: { ...p.curve_table, curve_columns: e.target.value } }))} data-testid="std-curve-cols"
                        placeholder="Curve table: Curve, Radius, Length, Delta, Chord Bearing, Chord, Tangent" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                    </div>
                    <div className="border-t border-[var(--border)] pt-2 flex flex-col gap-2">
                      <div className="text-[9px] font-mono text-[var(--text-dim)]">Report branding (closure report header)</div>
                      <Input value={std.branding.firm} onChange={(e) => setStd((p) => ({ ...p, branding: { ...p.branding, firm: e.target.value } }))} data-testid="std-firm"
                        placeholder={`Firm name (default: ${clientName || "client name"})`} className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                      <div className="flex items-center gap-2">
                        <input ref={logoInput} type="file" accept="image/*" className="hidden" data-testid="std-logo-file" onChange={onLogoFile} />
                        <button onClick={() => logoInput.current?.click()} data-testid="std-logo-upload"
                          className="h-7 px-2 rounded-[3px] border border-[var(--border)] text-[11px] font-mono text-[var(--text)] hover:border-[var(--primary)] hover:text-[var(--primary)]">
                          Upload logo
                        </button>
                        {std.branding.logo ? (
                          <>
                            <img src={std.branding.logo} alt="logo" className="h-7 max-w-[90px] object-contain border border-[var(--border)] rounded-[2px] bg-white" data-testid="std-logo-preview" />
                            <button onClick={() => setStd((p) => ({ ...p, branding: { ...p.branding, logo: "" } }))} data-testid="std-logo-clear"
                              className="text-[11px] font-mono text-[var(--alert)] hover:underline">remove</button>
                          </>
                        ) : (
                          <span className="text-[10px] font-mono text-[var(--text-dim)]">PNG/JPG, under 1 MB</span>
                        )}
                      </div>
                    </div>
                    <div className="border-t border-[var(--border)] pt-2 flex flex-col gap-2">
                      <div className="text-[9px] font-mono text-[var(--text-dim)]">Closure-report defaults (auto-fill the report fields)</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={std.report_defaults.project} onChange={(e) => setStd((p) => ({ ...p, report_defaults: { ...p.report_defaults, project: e.target.value } }))} data-testid="std-rd-project"
                          placeholder="Default project" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                        <Input value={std.report_defaults.parcel} onChange={(e) => setStd((p) => ({ ...p, report_defaults: { ...p.report_defaults, parcel: e.target.value } }))} data-testid="std-rd-parcel"
                          placeholder="Default parcel / lot" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                        <Input value={std.report_defaults.job_no} onChange={(e) => setStd((p) => ({ ...p, report_defaults: { ...p.report_defaults, job_no: e.target.value } }))} data-testid="std-rd-jobno"
                          placeholder="Default job no." className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                        <Input value={std.report_defaults.prepared_by} onChange={(e) => setStd((p) => ({ ...p, report_defaults: { ...p.report_defaults, prepared_by: e.target.value } }))} data-testid="std-rd-preparedby"
                          placeholder="Default prepared by" className="h-7 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
                      </div>
                    </div>
                    <Button size="sm" onClick={() => onSaveStandards(std)} data-testid="save-standards-btn"
                      className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white self-start">
                      <FloppyDisk size={14} weight="bold" /> Save standards
                    </Button>
                  </div>
                )}
              </div>
            )}
            {onSaveDefaults && (
              <Button variant="outline" onClick={() => { onSaveDefaults(); }} data-testid="save-defaults-btn"
                className="w-full h-9 rounded-[3px] gap-2 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)] text-[var(--text)]">
                <FloppyDisk size={16} weight="bold" /> Save current targets & scale as this client's defaults
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
